import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { MessageSquare, Users, Search, Shield } from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import { useCommunityStore } from '@/stores/communityStore'
import type { Board } from '@/stores/communityStore'
import { TeamFlairBadge } from '@/components/ui/TeamFlairBadge'

const CommunityPage: React.FC = () => {
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<'all' | 'team' | 'conference' | 'topic'>('all')
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const { boards, isLoading: loading, error, fetchBoards } = useCommunityStore()

  useEffect(() => {
    fetchBoards(user?.id)
  }, [user])

  const filtered = boards.filter(b => {
    if (filter !== 'all' && b.type !== filter) return false
    if (search) {
      const s = search.toLowerCase()
      return b.name.toLowerCase().includes(s) || (b.conference || '').toLowerCase().includes(s)
    }
    return true
  })

  const myBoards = filtered.filter(b => b.membership)
  const otherBoards = filtered.filter(b => !b.membership)

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="h-8 w-8 border-2 border-primary-400/30 border-t-primary-400 rounded-full animate-spin" />
      </div>
    )
  }

  if (error === 'coming_soon') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="mx-auto w-16 h-16 bg-primary-500/20 rounded-full flex items-center justify-center mb-4">
            <MessageSquare className="h-8 w-8 text-primary-400" />
          </div>
          <h2 className="text-2xl font-heading font-bold text-white mb-2">Community Boards</h2>
          <p className="text-white/50 text-sm">Coming soon! Team forums, game threads, and fan discussions are on the way.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-transparent px-4 py-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-heading font-bold text-white mb-2">Community</h1>
          <p className="text-white/40 text-sm">Join team boards, discuss games, and talk hoops with fellow fans.</p>
        </div>

        {/* Search + Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/30" />
            <input
              type="text"
              placeholder="Search boards..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.08] text-white text-sm placeholder:text-white/20 focus:outline-none focus:border-primary-500/50"
            />
          </div>
          <div className="flex gap-2">
            {(['all', 'team', 'conference', 'topic'] as const).map(f => (
              <button
                type="button"
                key={f}
                onClick={() => setFilter(f)}
                aria-pressed={filter === f}
                className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                  filter === f
                    ? 'bg-primary-500/20 text-primary-400 border border-primary-500/30'
                    : 'bg-white/[0.04] text-white/40 border border-white/[0.06] hover:bg-white/[0.08]'
                }`}
              >
                {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* My Boards */}
        {myBoards.length > 0 && (
          <div className="mb-8">
            <h2 className="text-sm font-medium text-white/30 uppercase tracking-wider mb-3">Your Boards</h2>
            <div className="grid gap-3">
              {myBoards.map(board => (
                <React.Fragment key={board.id}>
                  <BoardCard board={board} onClick={() => navigate(`/community/${board.slug}`)} />
                  {board.subforums && board.subforums.length > 0 && (
                    <div className="ml-6 grid gap-2 border-l-2 border-white/[0.06] pl-3">
                      {board.subforums.map((sub: Board) => (
                        <BoardCard key={sub.id} board={sub} onClick={() => navigate(`/community/${sub.slug}`)} compact />
                      ))}
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

        {/* Other Boards */}
        <div>
          <h2 className="text-sm font-medium text-white/30 uppercase tracking-wider mb-3">
            {myBoards.length > 0 ? 'Explore Boards' : 'All Boards'}
          </h2>
          {otherBoards.length === 0 && !search ? (
            <div className="text-center py-12 text-white/30 text-sm">No boards available yet.</div>
          ) : otherBoards.length === 0 && search ? (
            <div className="text-center py-12 text-white/30 text-sm">No boards match "{search}"</div>
          ) : (
            <div className="grid gap-3">
              {otherBoards.map(board => (
                <React.Fragment key={board.id}>
                  <BoardCard board={board} onClick={() => navigate(`/community/${board.slug}`)} />
                  {board.subforums && board.subforums.length > 0 && (
                    <div className="ml-6 grid gap-2 border-l-2 border-white/[0.06] pl-3">
                      {board.subforums.map((sub: Board) => (
                        <BoardCard key={sub.id} board={sub} onClick={() => navigate(`/community/${sub.slug}`)} compact />
                      ))}
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const BoardCard: React.FC<{ board: Board; onClick: () => void; compact?: boolean }> = ({ board, onClick, compact }) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full text-left flex items-center gap-4 ${compact ? 'p-3' : 'p-4'} rounded-xl bg-white/[0.03] border border-white/[0.06] hover:bg-white/[0.06] hover:border-white/[0.12] transition-all group focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400/70`}
  >
    {/* Icon */}
    <div className="flex-shrink-0">
      {board.teamId ? (
        <TeamFlairBadge teamId={board.teamId} size="md" />
      ) : (
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-500/30 to-accent-500/30 flex items-center justify-center">
          {board.type === 'conference' ? (
            <Shield className="h-5 w-5 text-white/50" />
          ) : (
            <MessageSquare className="h-5 w-5 text-white/50" />
          )}
        </div>
      )}
    </div>

    {/* Info */}
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2">
        <h3 className="text-sm font-semibold text-white group-hover:text-primary-400 transition-colors truncate">{board.name}</h3>
        {board.membership && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary-500/20 text-primary-400 flex-shrink-0">Joined</span>
        )}
      </div>
      {board.description && (
        <p className="text-xs text-white/30 truncate mt-0.5">{board.description}</p>
      )}
    </div>

    {/* Stats */}
    <div className="flex-shrink-0 flex items-center gap-4 text-[11px] text-white/25">
      <span className="flex items-center gap-1"><Users className="h-3 w-3" />{board.memberCount}</span>
      <span className="flex items-center gap-1"><MessageSquare className="h-3 w-3" />{board.postCount}</span>
    </div>
  </button>
)

export { CommunityPage }
